export type WorkspaceRole = "admin" | "manager" | "staff" | "reports" | "task_user";

export function workspaceRoleLabel(role: WorkspaceRole): string {
  if (role === "admin") return "Administrator";
  if (role === "manager") return "Manager";
  if (role === "reports") return "Reports · read only";
  if (role === "task_user") return "Assigned tasks";
  return "Source outreach";
}

export function canAssignTasks(role: WorkspaceRole): boolean {
  return role === "admin" || role === "manager";
}

export function canManageEvents(role: WorkspaceRole): boolean {
  return role === "admin" || role === "manager";
}

export function canViewAllEvents(role: WorkspaceRole): boolean {
  return canManageEvents(role) || role === "reports";
}

export function canViewAllSources(role: WorkspaceRole): boolean {
  return role === "admin" || role === "reports";
}

export function canViewTeamReports(role: WorkspaceRole): boolean {
  return role === "admin" || role === "reports";
}

export function canCreateSources(role: WorkspaceRole): boolean {
  return role === "admin" || role === "manager" || role === "staff";
}

export function canCreateTasks(role: WorkspaceRole): boolean {
  return role === "admin" || role === "manager" || role === "staff";
}

export function canCollaborateOnSources(role: WorkspaceRole): boolean {
  return role !== "reports" && role !== "task_user";
}

export function canLogSourceActivity(
  role: WorkspaceRole,
  userId: string,
  ownerUserId: string | null,
): boolean {
  if (role === "reports") return false;
  if (role === "task_user") return ownerUserId === userId;
  return true;
}

export function canAccessSource(role: WorkspaceRole, userId: string, ownerUserId: string | null): boolean {
  if (role === "reports" || role === "task_user") return false;
  return role === "admin" || ownerUserId === userId;
}

export function canUpdateSource(
  role: WorkspaceRole,
  userId: string,
  ownerUserId: string | null,
  requestedFields: string[],
): boolean {
  if (role === "reports" || role === "task_user") return false;
  if (canAccessSource(role, userId, ownerUserId)) return true;
  const changedFields = requestedFields.filter((field) => field !== "version");
  return changedFields.length > 0
    && changedFields.every((field) => field === "description" || field === "descriptionColor");
}

export function ownerForNewSource(
  role: WorkspaceRole,
  userId: string,
  requestedOwnerUserId: string | null | undefined,
): string {
  return role === "admin" && requestedOwnerUserId ? requestedOwnerUserId : userId;
}

export function canChooseSourceOwner(role: WorkspaceRole): boolean {
  return role === "admin";
}
